import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  AlertCircle,
  Camera,
  CheckCircle,
  Clock,
  LogIn,
  LogOut,
  LucideAngularModule,
  RefreshCw,
  Scan,
  UserCheck,
  UserX,
  MapPin,
} from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { AttendanceDto } from '../../core/models/attendance.models';
import { AttendanceService } from '../../core/services/attendance.service';
import { AuthService } from '../../core/services/auth.service';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const faceapi: any;

const MODEL_URL = 'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights';
const FACE_MATCH_THRESHOLD = 0.55;

type AppStep =
  | 'models-loading'
  | 'login'
  | 'ready'
  | 'registering'
  | 'scanning'
  | 'processing'
  | 'done'
  | 'error';

@Component({
  selector: 'app-check-in',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TranslatePipe],
  templateUrl: './check-in.component.html',
})
export class CheckInComponent implements OnInit, OnDestroy {
  @ViewChild('videoEl') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl') canvasRef!: ElementRef<HTMLCanvasElement>;

  private authService = inject(AuthService);
  private attendanceService = inject(AttendanceService);
  private readonly destroyRef = inject(DestroyRef);

  AlertCircle = AlertCircle;
  Camera = Camera;
  CheckCircle = CheckCircle;
  Clock = Clock;
  LogIn = LogIn;
  LogOut = LogOut;
  RefreshCw = RefreshCw;
  Scan = Scan;
  UserCheck = UserCheck;
  UserX = UserX;
  MapPin = MapPin;

  step = signal<AppStep>('models-loading');
  modelProgress = signal(0);
  modelError = signal('');
  errorMsg = signal('');
  successMsg = signal('');

  // Camera
  cameraActive = signal(false);
  faceStatus = signal<'none' | 'detected' | 'matched' | 'no-match'>('none');
  private stream: MediaStream | null = null;
  private detectionInterval: ReturnType<typeof setInterval> | null = null;
  private pendingAction: 'checkin' | 'checkout' | 'register' | null = null;

  // Face
  storedDescriptor: Float32Array | null = null;
  hasFaceRegistered = signal(false);

  // Status
  currentRecord = signal<AttendanceDto | null>(null);

  // Login
  loginForm = { phoneNumber: '', password: '' };
  loginLoading = false;
  loginError = '';

  get user() {
    return this.authService.user();
  }

  get isCheckedIn() {
    const r = this.currentRecord();
    return r !== null && r.checkOutTime === null;
  }

  async ngOnInit() {
    await this.loadModels();
    if (!this.authService.isAuthenticated()) {
      this.step.set('login');
      return;
    }
    await this.afterLogin();
  }

  ngOnDestroy() {
    this.stopCamera();
  }

  // в”Ђв”Ђв”Ђ Model Loading в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  private async loadModels() {
    this.step.set('models-loading');
    try {
      if (typeof faceapi === 'undefined') {
        this.modelError.set('face-api.js yuklanmadi. Sahifani yangilang.');
        return;
      }
      this.modelProgress.set(10);
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      this.modelProgress.set(50);
      await faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL);
      this.modelProgress.set(80);
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
      this.modelProgress.set(100);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      this.modelError.set('Modellar yuklanishida xato. Internet aloqasini tekshiring.');
    }
  }

  private async afterLogin() {
    this.step.set('models-loading');
    this.attendanceService
      .getMyFaceDescriptor()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.data) {
            try {
              const arr = JSON.parse(res.data) as number[];
              this.storedDescriptor = new Float32Array(arr);
              this.hasFaceRegistered.set(true);
            } catch {
              this.hasFaceRegistered.set(false);
            }
          } else {
            this.hasFaceRegistered.set(false);
          }
        },
        error: () => {
          this.hasFaceRegistered.set(false);
          this.loadStoresAndStatus();
        },
        complete: () => {
          this.loadStoresAndStatus();
        },
      });
  }

  private loadStoresAndStatus() {
    this.attendanceService
      .getMyTodayStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.currentRecord.set(res.data ?? null),
        error: () => this.step.set('ready'),
        complete: () => this.step.set('ready'),
      });
  }

  // в”Ђв”Ђв”Ђ Login в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  doLogin() {
    this.loginError = '';
    this.loginLoading = true;
    this.authService
      .login(this.loginForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (res) => {
          this.loginLoading = false;
          if (res.success) {
            await this.afterLogin();
          } else {
            this.loginError = res.message ?? res.errors?.join(' ') ?? 'Xato yuz berdi';
          }
        },
        error: (err) => {
          this.loginLoading = false;
          this.loginError = err?.error?.message || "Login yoki parol noto'g'ri";
        },
      });
  }

  // в”Ђв”Ђв”Ђ Camera в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  async startCamera(action: 'checkin' | 'checkout' | 'register') {
    this.pendingAction = action;
    this.errorMsg.set('');
    this.successMsg.set('');
    this.faceStatus.set('none');

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
      });
      this.cameraActive.set(true);
      this.step.set('scanning');

      // Give DOM a tick to render video element
      setTimeout(() => {
        const video = this.videoRef?.nativeElement;
        if (video) {
          video.srcObject = this.stream;
          video.play().then(() => this.startDetectionLoop());
        }
      }, 100);
    } catch {
      this.errorMsg.set('Kamera ochilmadi. Ruxsat bering yoki boshqa brauzer ishlating.');
    }
  }

  stopCamera() {
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this.detectionInterval = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.cameraActive.set(false);
    this.faceStatus.set('none');
  }

  cancelScan() {
    this.stopCamera();
    this.step.set('ready');
    this.pendingAction = null;
  }

  // в”Ђв”Ђв”Ђ Face Detection Loop в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  private startDetectionLoop() {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas) return;

    this.detectionInterval = setInterval(async () => {
      if (!video || video.readyState < 2) return;

      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      try {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.5 }))
          .withFaceLandmarks(true)
          .withFaceDescriptor();

        if (!detection) {
          this.faceStatus.set('none');
          return;
        }

        // Draw bounding box
        const { x, y, width, height } = detection.detection.box;
        const scaleX = canvas.width / (video.videoWidth || 640);
        const scaleY = canvas.height / (video.videoHeight || 480);
        const matched = this.isFaceMatch(detection.descriptor);
        ctx.strokeStyle = matched ? '#22c55e' : '#ef4444';
        ctx.lineWidth = 3;
        ctx.strokeRect(x * scaleX, y * scaleY, width * scaleX, height * scaleY);

        // Face label
        ctx.fillStyle = matched ? '#22c55e' : '#ef4444';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText(
          matched
            ? 'вњ“ Yuz tasdiqlandi'
            : this.pendingAction === 'register'
              ? 'вњ“ Yuz aniqlandi'
              : 'вњ— Mos kelmadi',
          x * scaleX,
          y * scaleY > 20 ? y * scaleY - 8 : y * scaleY + height * scaleY + 20,
        );

        if (this.pendingAction === 'register') {
          this.faceStatus.set('detected');
          // Auto-capture after stable detection
          await this.captureAndRegister(detection.descriptor);
        } else if (matched) {
          this.faceStatus.set('matched');
          await this.proceedWithGps();
        } else {
          this.faceStatus.set('no-match');
        }
      } catch {
        // detection error  skip frame
      }
    }, 300);
  }

  private isFaceMatch(descriptor: Float32Array): boolean {
    if (!this.storedDescriptor) return this.pendingAction === 'register';
    const dist = faceapi.euclideanDistance(descriptor, this.storedDescriptor);
    return dist < FACE_MATCH_THRESHOLD;
  }

  // в”Ђв”Ђв”Ђ Register Face в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  private registering = false;

  private async captureAndRegister(descriptor: Float32Array) {
    if (this.registering) return;
    this.registering = true;

    clearInterval(this.detectionInterval!);
    this.detectionInterval = null;

    const arr = Array.from(descriptor);
    const json = JSON.stringify(arr);

    this.attendanceService
      .registerFace(json)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.storedDescriptor = descriptor;
          this.hasFaceRegistered.set(true);
          this.successMsg.set("Yuzingiz muvaffaqiyatli ro'yxatdan o'tkazildi!");
          this.stopCamera();
          this.step.set('ready');
          this.registering = false;
        },
        error: (err) => {
          this.errorMsg.set(err?.error?.message || 'Saqlashda xato');
          this.stopCamera();
          this.step.set('ready');
          this.registering = false;
        },
      });
  }

  // в”Ђв”Ђв”Ђ GPS + API Call в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  private proceeding = false;

  private async proceedWithGps() {
    if (this.proceeding) return;
    this.proceeding = true;

    clearInterval(this.detectionInterval!);
    this.detectionInterval = null;
    this.step.set('processing');
    this.stopCamera();

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;

        if (this.pendingAction === 'checkin') {
          this.attendanceService
            .checkIn({ lat, lon })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (res) => {
                this.currentRecord.set(res.data);
                this.successMsg.set('Ishga kelish qayd etildi!');
                this.step.set('done');
                this.proceeding = false;
                setTimeout(() => this.step.set('ready'), 3000);
              },
              error: (err) => {
                this.errorMsg.set(err?.error?.message || 'Xato yuz berdi');
                this.step.set('ready');
                this.proceeding = false;
              },
            });
        } else {
          this.attendanceService
            .checkOut({ lat, lon })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (res) => {
                this.currentRecord.set(res.data);
                this.successMsg.set('Ketish qayd etildi!');
                this.step.set('done');
                this.proceeding = false;
                setTimeout(() => this.step.set('ready'), 3000);
              },
              error: (err) => {
                this.errorMsg.set(err?.error?.message || 'Xato yuz berdi');
                this.step.set('ready');
                this.proceeding = false;
              },
            });
        }
      },
      (err) => {
        this.errorMsg.set('Joylashuv aniqlanmadi: ' + err.message);
        this.step.set('ready');
        this.proceeding = false;
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  // в”Ђв”Ђв”Ђ Helpers в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  logout() {
    this.stopCamera();
    this.authService.logout();
    this.step.set('login');
  }

  formatTime(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
  }

  calcDuration(checkIn: string, checkOut: string | null): string {
    if (!checkOut) return '';
    const diff = Math.floor((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 60000);
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}s ${m}d`;
  }
}
